import React from 'react';
import { BookOpen, CheckCircle2, Search, ShieldCheck, Users } from 'lucide-react';
import { unitService } from '../../../services/unitService';

const DepartmentTrainerManagement = ({ homeDepartmentId, homeDepartmentName, onOpenUnits, onOpenApprovals, onBack = null, roleBadge = '' }) => {
  const [trainers, setTrainers] = React.useState([]);
  const [units, setUnits] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [department, setDepartment] = React.useState('all');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [trainerRows, unitRows] = await Promise.all([
        unitService.getAssignableTrainers(),
        unitService.getAll()
      ]);
      const loadedTrainers = trainerRows || [];
      // Filter out trainers not in the home department, if a home department is specified
      const deptTrainers = homeDepartmentId 
        ? loadedTrainers.filter(t => String(t.department?.id || t.department_id) === String(homeDepartmentId))
        : loadedTrainers;
        
      setTrainers(deptTrainers);
      setUnits(unitRows || []);
    } catch (loadError) {
      setError(loadError?.message || 'Trainer records could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [homeDepartmentId]);

  React.useEffect(() => { load(); }, [load]);

  const departments = [...new Map(trainers
    .filter(trainer => trainer.department?.id)
    .map(trainer => [trainer.department.id, trainer.department]))
    .values()]
    .sort((a, b) => a.name.localeCompare(b.name));

  const workload = units.reduce((map, unit) => {
    const trainerId = unit.trainerAssignment?.trainer?.id;
    if (!trainerId) return map;
    if (!map[trainerId]) map[trainerId] = { active: 0, pending: 0 };
    const key = String(unit.trainerAssignment?.status || '').toLowerCase() === 'pending' ? 'pending' : 'active';
    map[trainerId][key] += 1;
    return map;
  }, {});

  const filtered = trainers.filter(trainer => {
    const term = search.trim().toLowerCase();
    const matchesSearch = !term || [trainer.full_name, trainer.institutional_email, trainer.email, trainer.employee_id]
      .some(value => String(value || '').toLowerCase().includes(term));
    const matchesDepartment = department === 'all' || String(trainer.department?.id || trainer.department_id) === department;
    return matchesSearch && matchesDepartment;
  });

  const homeCount = trainers.filter(trainer => String(trainer.department?.id || trainer.department_id) === String(homeDepartmentId)).length;
  const pendingCount = Object.values(workload).reduce((sum, row) => sum + row.pending, 0);

  return <div className="space-y-4">
    {/* Single-line Minimalistic Toolbar Header */}
    <div className="flex flex-wrap lg:flex-nowrap items-center justify-between gap-3 rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm">
      <div className="flex items-center gap-2.5 shrink-0">
        {onBack && (
          <>
            <button onClick={onBack} className="text-xs font-semibold text-blue-700 hover:underline">← Staff dashboard</button>
            <span className="text-slate-300 text-xs">/</span>
          </>
        )}
        <h1 className="text-sm font-bold tracking-tight text-slate-900">Trainer Management</h1>
        {roleBadge && (
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600 capitalize">
            {roleBadge}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 flex-1 justify-end">
        <button onClick={onOpenApprovals} className="h-8 text-xs font-semibold px-3 rounded-lg border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 transition-colors shadow-2xs">
          Review approvals
        </button>
        <button onClick={onOpenUnits} className="h-8 text-xs font-semibold px-3 rounded-lg bg-slate-950 text-white hover:bg-slate-800 transition-colors shadow-2xs">
          Assign through units
        </button>
      </div>
    </div>

    <section className="grid gap-3 sm:grid-cols-3">
      {[
        ['Active trainers', trainers.length, Users, 'bg-blue-100 text-blue-700'],
        ['Mother department', homeCount, ShieldCheck, 'bg-emerald-100 text-emerald-700'],
        ['Awaiting approval', pendingCount, CheckCircle2, 'bg-amber-100 text-amber-700']
      ].map(([label, value, Icon, tone]) => <div key={label} className="flex items-center gap-3 rounded-xl border bg-white p-4 shadow-sm"><span className={`grid h-10 w-10 place-items-center rounded-lg ${tone}`}><Icon className="h-5 w-5" /></span><div><strong className="block text-xl">{value}</strong><span className="text-xs text-slate-500">{label}</span></div></div>)}
    </section>

    <section className="rounded-xl border bg-white shadow-sm">
      <div className="flex flex-wrap gap-3 border-b p-4">
        <label className="relative min-w-64 flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400"/><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search name, email or PF number" className="w-full rounded-lg border py-2 pl-9 pr-3 text-sm"/></label>
        {!homeDepartmentId && (
          <select value={department} onChange={event => setDepartment(event.target.value)} className="rounded-lg border px-3 py-2 text-sm"><option value="all">All mother departments</option>{departments.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
        )}
      </div>
      {error && <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
      {loading ? <p className="p-8 text-center text-sm text-slate-500">Loading trainers…</p> : <div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Trainer</th>{!homeDepartmentId && <th className="px-4 py-3">Mother department</th>}<th className="px-4 py-3">Scoped units</th><th className="px-4 py-3">Max Limits</th><th className="px-4 py-3">Approval</th></tr></thead><tbody>{filtered.map(trainer => { 
        const count = workload[trainer.id] || { active: 0, pending: 0 }; 
        const isHome = String(trainer.department?.id || trainer.department_id) === String(homeDepartmentId); 
        return <tr key={trainer.id} className="border-t">
          <td className="px-4 py-3"><strong>{trainer.full_name || 'Unnamed staff member'}</strong><div className="text-xs text-slate-500">{trainer.employee_id || 'No PF'} · {trainer.institutional_email || trainer.email || 'No email'}</div></td>
          {!homeDepartmentId && <td className="px-4 py-3">{trainer.department?.name || 'Not assigned'}</td>}
          <td className="px-4 py-3"><span className="inline-flex items-center gap-1"><BookOpen className="h-4 w-4 text-slate-400"/>{trainer.current_units !== undefined ? trainer.current_units : count.active} active</span>{trainer.current_hours > 0 && <span className="text-xs text-slate-500 block mt-1">{trainer.current_hours} hrs/week</span>}</td>
          <td className="px-4 py-3">
            {isHome ? (
               <div className="flex flex-col gap-1 text-xs">
                 <div className="flex items-center gap-2">
                   <label className="text-slate-500 w-10">Units:</label>
                   <input type="number" min="1" className="border rounded px-2 py-0.5 w-16" defaultValue={trainer.max_workload_units || ''} onBlur={async (e) => {
                     try { await unitService.updateTrainerWorkload(trainer.id, { maxUnits: e.target.value ? parseInt(e.target.value) : null, maxHours: trainer.max_workload_hours }); load(); } catch(err) { console.error(err); setError('Units update failed: ' + (err.message || 'Unknown error')); }
                   }} />
                 </div>
                 <div className="flex items-center gap-2">
                   <label className="text-slate-500 w-10">Hours:</label>
                   <input type="number" min="1" className="border rounded px-2 py-0.5 w-16" defaultValue={trainer.max_workload_hours || ''} onBlur={async (e) => {
                     try { await unitService.updateTrainerWorkload(trainer.id, { maxUnits: trainer.max_workload_units, maxHours: e.target.value ? parseInt(e.target.value) : null }); load(); } catch(err) { console.error(err); setError('Hours update failed: ' + (err.message || 'Unknown error')); }
                   }} />
                 </div>
               </div>
            ) : (
               <div className="text-xs text-slate-500">
                 {trainer.max_workload_units ? `${trainer.max_workload_units} units max` : 'No unit limit'}<br/>
                 {trainer.max_workload_hours ? `${trainer.max_workload_hours} hrs max` : 'No hour limit'}
               </div>
            )}
          </td>
          <td className="px-4 py-3">{count.pending ? <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700">{count.pending} pending</span> : <span className="text-slate-400">Clear</span>}</td>
        </tr>; 
      })}</tbody></table>{!filtered.length && <p className="p-8 text-center text-sm text-slate-500">No trainers match these filters.</p>}</div>}
    </section>
  </div>;
};

export default DepartmentTrainerManagement;
